// src/pages/income/service/IncomeServiceNew.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import useBackdropClose from "../../../hooks/useBackdropClose";
import styles from "./IncomeServiceNew.module.css";
import api from "../../../api";
import { useTabActivated } from "../../../tabs/TabActivation";
import InvoiceForm from "./InvoiceForm";  // 청구서
import StatementForm from "./StatementForm"; // 거래명세서

const PAY_METHOD_LABELS = ["계좌이체", "카드결제", "현금결제"];
const PAY_METHOD_TO_CODE = { "계좌이체": "transfer", "카드결제": "card", "현금결제": "cash" };
const CODE_TO_PAY_METHOD = { transfer: "계좌이체", card: "카드결제", cash: "현금결제" };

export default function IncomeServiceNew({ fixedCategory = "용역사업" }) {
  const gisungBackdrop = useBackdropClose(() => setShowGisungModal(false));

  const [activeTab, setActiveTab] = useState("draft");
  const [category] = useState(fixedCategory);
  const [subcategory, setSubcategory] = useState("용역");


  // ✅ 작성일/청구일: 연/월/일 분리 (연도만 올해로 미리, 월·일은 빈칸)
  const thisYear = String(new Date().getFullYear());
  const [draftY, setDraftY] = useState(thisYear);
  const [draftM, setDraftM] = useState("");
  const [draftD, setDraftD] = useState("");
  const [claimY, setClaimY] = useState(thisYear);
  const [claimM, setClaimM] = useState("");
  const [claimD, setClaimD] = useState("");

  // ✅ 3칸 → "YYYY-MM-DD" 합성 (월·일 다 있어야 유효)
  const draftDate = useMemo(
    () => (draftY && draftM && draftD
      ? `${draftY}-${String(draftM).padStart(2, "0")}-${String(draftD).padStart(2, "0")}`
      : ""),
    [draftY, draftM, draftD]
  );
  const claimDate = useMemo(
    () => (claimY && claimM && claimD
      ? `${claimY}-${String(claimM).padStart(2, "0")}-${String(claimD).padStart(2, "0")}`
      : ""),
    [claimY, claimM, claimD]
  );

  const [workMonth, setWorkMonth] = useState("");
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");
  const [title, setTitle] = useState("");
  const [seq, setSeq] = useState("001");
  const [seq2, setSeq2] = useState("002");  // ✅ 공문 번호


  const [autoSeq, setAutoSeq] = useState(1);  // ✅ 자동채번 원본 보관


  const [loadingSeq, setLoadingSeq] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);

  // ✅ 거래처 관련 상태
  const [vendors, setVendors] = useState([]);
  const [vendorInput, setVendorInput] = useState("");
  const [vendorId, setVendorId] = useState(null);
  const [showVendorList, setShowVendorList] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);

  const [payMethodLabel, setPayMethodLabel] = useState("계좌이체");


  // ✅ 기성계 불러오기 모달 상태
  const [showGisungModal, setShowGisungModal] = useState(false);
  const [gisungRecords, setGisungRecords] = useState([]);
  const [gisungLoading, setGisungLoading] = useState(false);
  const [gisungYear, setGisungYear] = useState(new Date().getFullYear());
  const [gisungMonth, setGisungMonth] = useState("");  // ✅ ""=전체
  const [pickedGisungId, setPickedGisungId] = useState(null);   // ✅ 폼에 불러온(작성중) record id
  const [pickedGisungRec, setPickedGisungRec] = useState(null);  // ✅ 선택한 기성계 원본 보관


  const [savedGisungIds, setSavedGisungIds] = useState([]);     // ✅ 저장 완료된 record id 목록


  // ✅ 월 필터 적용된 목록
  const filteredGisungRecords = gisungMonth
    ? gisungRecords.filter((r) => String(r.gisung_month) === String(gisungMonth))
    : gisungRecords;


  const yymmdd = useMemo(
    () => (draftDate ? draftDate.replaceAll("-", "").slice(2) : ""),
    [draftDate]
  );


  // ✅ 내부기안(001) / 공문(002) 두 개의 기안번호 미리보기
  const seqNum = useMemo(() => parseInt(seq, 10) || 1, [seq]);
  const draftNoInternal = useMemo(
    () => `금천일자리(주)용역-${yymmdd}-${String(seqNum).padStart(3, "0")}호`,
    [yymmdd, seqNum]
    );
  const draftNoOfficial = useMemo(
    () => `금천일자리(주)용역-${yymmdd}-${String(parseInt(seq2, 10) || seqNum + 1).padStart(3, "0")}호`,
    [yymmdd, seq2, seqNum]
  );

  // ✅ 작성일 변경 시 청구일 자동 동기화 (연/월/일 각각 복사)
  useEffect(() => {
    setClaimY(draftY);
    setClaimM(draftM);
    setClaimD(draftD);
  }, [draftY, draftM, draftD]);





  // ✅ seq 자동 생성 (용역사업용)
  // ✅ keep-alive 탭 재방문 시 다시 조회한다(마운트가 유지돼 처음 조회값에 멈춰 있지 않게).
  // 같은 날짜의 재조회에서는 사용자가 직접 바꿔 둔 시작 번호를 덮지 않는다.
  const [seqRefreshTick, setSeqRefreshTick] = useState(0);
  useTabActivated(() => setSeqRefreshTick((t) => t + 1));
  const seqFetchRef = useRef({ key: null, auto: null });
  const currentSeqRef = useRef(seq);
  currentSeqRef.current = seq;
  useEffect(() => {
    let ignore = false;
    async function getSeq() {
      if (!category || !draftDate) return;
      setLoadingSeq(true);

      try {
        const { data } = await api.get(`/income-service/next-seq/?date=${draftDate}`);
        if (!ignore) {
          const key = `${category}|${draftDate}`;
          const prev = seqFetchRef.current;
          seqFetchRef.current = { key, auto: data.next_seq };
          setAutoSeq(data.next_seq);
          const keepManual = prev.key === key && currentSeqRef.current !== String(prev.auto).padStart(3, "0");
          if (!keepManual) {
            setSeq(String(data.next_seq).padStart(3, "0"));
            setSeq2(String(data.next_seq2 ?? data.next_seq + 1).padStart(3, "0"));
          }
        }
      } catch (e) {

        console.error("[next-seq] failed:", e?.response?.status);
      } finally {
        if (!ignore) setLoadingSeq(false);
      }
    }
    getSeq();
    return () => { ignore = true; };
  }, [category, draftDate, seqRefreshTick]);

  // ✅ 거래처 목록 불러오기
  useEffect(() => {
    async function fetchVendors() {
      setLoadingVendors(true);
      try {
        const res = await api.get("vendors/");
        const data = Array.isArray(res.data) ? res.data : res.data.results || [];
        setVendors(data);
      } catch (err) {
        console.error("❌ 거래처 목록 불러오기 실패:", err);
      } finally {
        setLoadingVendors(false);
      }
    }
    fetchVendors();
  }, []);

  // ✅ 거래처 자동완성 필터
  const filteredVendors = vendors.filter((v) =>
    v.name.toLowerCase().includes(vendorInput.toLowerCase())
  );

  // ✅ 거래처 입력 시
  const handleChangeVendor = (e) => {
    setVendorInput(e.target.value);
    setVendorId(null);
    setShowVendorList(true);
    setHighlightIndex(-1);
  };

  // ✅ 거래처 선택
  const handlePickVendor = (v) => {
    setVendorId(v.id);
    setVendorInput(v.name);
    setShowVendorList(false);
    setHighlightIndex(-1);
  };

  // ✅ 방향키 이동 및 Enter 선택
  const handleVendorKeyDown = (e) => {
    if (!showVendorList || filteredVendors.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev < filteredVendors.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev > 0 ? prev - 1 : filteredVendors.length - 1
      );


    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightIndex >= 0 && filteredVendors[highlightIndex]) {
        handlePickVendor(filteredVendors[highlightIndex]);
      }
    }
  };

  // ✅ ───────────── 여기부터 새로 추가 ─────────────
  // 기성계 이력 불러오기 (모달 열 때 / 연도 변경 시)
  useEffect(() => {
    if (!showGisungModal) return;
    let ignore = false;
    (async () => {
      setGisungLoading(true);
      try {
        const res = await api.get("gisung-gye/records/", {
          params: { year: gisungYear },
        });
        const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
        if (!ignore) setGisungRecords(data);
      } catch (err) {
        console.error("❌ 기성계 이력 불러오기 실패:", err);
        if (!ignore) setGisungRecords([]);
      } finally {
        if (!ignore) setGisungLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [showGisungModal, gisungYear]);

  // 기성계 record 선택 → 폼 자동 채움
  const pickGisungRecord = (rec) => {
    // 1) 작업월(귀속월): 기성 연/월
    setWorkMonth(`${rec.gisung_year}-${String(rec.gisung_month).padStart(2, "0")}`);

    // 2) 금액: 금회기성금액
    setAmount(String(rec.current_amount ?? ""));

    // 3) 거래처: 발주처/기관명(institution) ↔ 거래처 DB 매칭
    const instName = (rec.institution || "").trim();
    const matched = instName
      ? vendors.find((v) => (v.name || "").trim() === instName)
      : null;

    if (matched) {
      setVendorInput(matched.name);
      setVendorId(matched.id);
      setShowVendorList(false);
    } else {
      // 매칭 실패 시 거래처를 비우지 않고 기존 값 유지
      // institution 값을 힌트로 표시만 해줌
      setShowVendorList(false);
      alert(`기성계 거래처(${instName})와 일치하는 항목이 없습니다.\n거래처를 직접 선택해 주세요.`);
    }



    setPickedGisungId(rec.id);   // ✅ 지금 작성 중인 기성계 record 기억
    setPickedGisungRec(rec);  // ✅ 원본 보관


    setShowGisungModal(false);

  };

  // ✅ 제목 자동 생성
  useEffect(() => {
    if (vendorInput && subcategory && workMonth) {
      let year = "", month = "";

      if (workMonth.includes("-")) {
        [year, month] = workMonth.split("-");
      } else if (workMonth.includes("년")) {
        year = workMonth.split("년")[0].trim();
        month = workMonth.split("년")[1].replace("월", "").trim();
      }

      if (month && month.length === 1) month = "0" + month;

      // ✅ 거래처명에서 괄호() 제거
      const cleanVendor = vendorInput.replace(/\(.*?\)/g, "").trim();

      if (year && month) {
        setTitle(`${year}년 ${month}월 ${cleanVendor} ${subcategory} 청구서`);
      }
    } else {
      setTitle("");
    }
  }, [vendorInput, subcategory, workMonth]);

  // ✅ 저장 기능 (용역사업용)
  const onSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);

    if (!subcategory || !title || !amount || !vendorInput) {
      alert("필수 항목을 모두 입력해 주세요.");
      setSaving(false);
      return;
    }

    if (!draftDate) {
      alert("작성일의 월·일을 입력해 주세요.");
      setSaving(false);
      return;
    }

    let safeWorkMonth = workMonth;
    if (safeWorkMonth.includes("년")) {
      safeWorkMonth = safeWorkMonth
        .replace("년 ", "-")
        .replace("월", "")
        .trim();
    }
    // ✅ 결번 경고
    const currentSeq = parseInt(seq, 10) || 1;
    if (currentSeq > autoSeq) {
      const ok = window.confirm(
        `⚠️ ${String(autoSeq).padStart(3, "0")}호~${String(currentSeq - 1).padStart(3, "0")}호가 결번으로 남습니다.\n계속 진행하시겠습니까?`
      );
      if (!ok) { setSaving(false); return; }
    }

    const payload = {
      category,
      subcategory,
      title,
      draft_date: draftDate,
      claim_date: claimDate || null,
      work_month: safeWorkMonth,
      partner_id: vendorId || null,
      partner_name: vendorInput,
      amount: amount ? Number(amount) : 0,
      pay_method: PAY_METHOD_TO_CODE[payMethodLabel] || "transfer",
      desc: desc || "",
      start_seq: parseInt(seq, 10) || undefined,  // ✅ 수동 지정 시작 번호
    };

    try {
      // ✅ 내부기안 + 공문 2건 동시 생성
      const { data } = await api.post(`/income-service/create-pair/`, payload);
      const internal = Array.isArray(data)
        ? data.find((d) => d.draft_kind === "internal")
        : null;
      setSavedId(internal?.id ?? (Array.isArray(data) ? data[0]?.id : data?.id));

      const nos = Array.isArray(data) ? data.map((d) => d.draft_no).join("\n") : "";
      alert(
        `✅ 저장 완료! (내부기안 + 공문 2건)\n${nos}\n거래처: ${vendorInput}\n금액: ${Number(amount).toLocaleString()}원`
      );

      // ✅ 이번에 불러온 기성계 record를 "완료"로 기록
      if (pickedGisungId != null) {
        setSavedGisungIds((prev) =>
          prev.includes(pickedGisungId) ? prev : [...prev, pickedGisungId]
        );
        setPickedGisungId(null);
        setPickedGisungRec(null);  // ✅ 저장 완료 후 패널 초기화


      }

      // 저장 후 다음 기안번호 미리보기 갱신
      


      try {
        const { data: sd } = await api.get(`/income-service/next-seq/?date=${draftDate}`);
        setAutoSeq(sd.next_seq);
        setSeq(String(sd.next_seq).padStart(3, "0"));
        setSeq2(String(sd.next_seq2 ?? sd.next_seq + 1).padStart(3, "0"));
      } catch { }


    } catch (err) {
      console.error("❌ 저장 실패:", err.response?.data || err);
      alert("서버 저장 실패. 관리자에게 문의하세요.");
    } finally {
      setSaving(false);
    }
  };

  // ✅ 내보내기 기능
  const download = (fmt) => {
    if (!savedId) return alert("먼저 저장해 주세요.");
    window.open(`${api.defaults.baseURL}income-drafts/${savedId}/export?fmt=${fmt}`, "_blank");
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.pane}>
        {activeTab === "draft" && (
          <form className={styles.form} onSubmit={onSubmit}>

            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: "10px" }}>
              <h3 style={{ margin: 0, whiteSpace: "nowrap", fontSize: 16 }}>용역사업 수입기안서 작성</h3>

              
              <button
                type="button"
                onClick={() => setShowGisungModal(true)}
                className={styles.primaryBtn}
                style={{ background: "#2E7D32", padding: "6px 12px", fontSize: 13, whiteSpace: "nowrap", flexShrink: 0, flexGrow: 0, width: "auto", display: "inline-block" }}
                >
                📥 기성계 불러오기
              </button>


            </div>


            <Row label="시작 번호">
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="number"
                  min={1}
                  value={parseInt(seq, 10) || 1}

                  onChange={(e) => {
                    const v = Math.max(1, parseInt(e.target.value) || 1);
                    setSeq(String(v).padStart(3, "0"));
                    // 수동 변경 시 seq2는 단순히 +1로 표시 (실제 저장은 백엔드가 스킵 처리)
                    setSeq2(String(v + 1).padStart(3, "0"));
                  }}


                  style={{ width: 72, textAlign: "center" }}
                />
                {loadingSeq && (
                  <span style={{ fontSize: 12, color: "#9ca3af" }}>조회 중…</span>
                )}
                {!loadingSeq && (
                  <span style={{ fontSize: 12, color: "#6b7280" }}>
                    자동 채번 (빈 번호 있으면 직접 수정 가능)
                  </span>
                )}
              </div>
            </Row>

            <Row label="기안번호 (내부기안)">
              <input value={draftNoInternal} readOnly />
            </Row>

            <Row label="기안번호 (공문)">
              <input value={draftNoOfficial} readOnly />
            </Row>




            <Row label="제목(자동완성)">
              <input
                value={title}
                readOnly
                placeholder="청구일·작업월·거래처 입력 시 자동 생성됩니다."
              />
            </Row>

            <Row label="사업구분"><input value={category} readOnly /></Row>

            <Row label="세부구분">
              <select
                value={subcategory}
                onChange={(e) => setSubcategory(e.target.value)}
                className={styles.selectBox}
              >
                <option value="용역">용역</option>
                <option value="기타">기타</option>
              </select>
            </Row>

            <Row label="작성일">
              <YmdPicker
                y={draftY} m={draftM} d={draftD}
                setY={setDraftY} setM={setDraftM} setD={setDraftD}
              />
            </Row>

            <Row label="청구일">
              <YmdPicker
                y={claimY} m={claimM} d={claimD}
                setY={setClaimY} setM={setClaimM} setD={setClaimD}
              />
            </Row>

            <Row label="작업월(귀속월)">
              <input type="month" value={workMonth} onChange={(e) => setWorkMonth(e.target.value)} />
            </Row>

            <Row label="거래처">
              <div className={styles.partnerRow}>
                <input
                  placeholder="직접 입력 또는 검색"
                  value={vendorInput}
                  onChange={handleChangeVendor}
                  onKeyDown={handleVendorKeyDown}
                />
                <button
                  type="button"
                  className={styles.partnerButton}
                  onClick={() => setShowVendorList((v) => !v)}
                >
                  ▼
                </button>
              </div>
              {showVendorList && (
                <div className={styles.partnerBox}>
                  {loadingVendors ? (
                    <div>불러오는 중...</div>
                  ) : filteredVendors.length > 0 ? (
                    filteredVendors.map((v, i) => (
                      <div
                        key={v.id}
                        className={`${styles.partnerItem} ${i === highlightIndex ? styles.activeItem : ""}`}
                        onClick={() => handlePickVendor(v)}
                      >
                        {v.name}
                      </div>
                    ))
                  ) : (
                    <div>일치하는 거래처가 없습니다.</div>
                  )}
                </div>
              )}
            </Row>

            {pickedGisungRec && (
              <Row label="선택 자료">
                <div style={{
                  background: "#f0fdf4", border: "1px solid #86efac",
                  borderRadius: 8, padding: "10px 14px", fontSize: 13,
                  position: "relative",

                  color: "#166534",
                }}>
                  <div style={{
                    fontWeight: "bold", fontSize: 12, color: "#15803d",
                    marginBottom: 8, borderBottom: "1px solid #86efac", paddingBottom: 6,
                  }}>
                    📌 내가 선택한 자료
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "auto auto", gap: "4px 16px" }}>
                  <span style={{ color: "#6b7280" }}>기성월</span>
                  <span>{pickedGisungRec.gisung_year}.{String(pickedGisungRec.gisung_month).padStart(2, "0")}</span>
                  <span style={{ color: "#6b7280" }}>회차</span>
                  <span>{pickedGisungRec.gisung_round}회차</span>
                  <span style={{ color: "#6b7280" }}>거래처(발주처)</span>
                  <span>{pickedGisungRec.institution || "-"}</span>
                  <span style={{ color: "#6b7280" }}>용역명</span>
                  <span>{pickedGisungRec.service_name || "-"}</span>
                  <span style={{ color: "#6b7280" }}>금회기성금액</span>
                  <span style={{ fontWeight: "bold" }}>{Number(pickedGisungRec.current_amount).toLocaleString()}원</span>
                  </div>
                </div>
              </Row>
            )}


            <Row label="금액">
              <input
                inputMode="numeric"
                placeholder="숫자만"
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ""))}
              />
            </Row>

            <Row label="거래방법">
              <div className={styles.inline}>
                {PAY_METHOD_LABELS.map((label) => (
                  <label key={label} className={styles.check}>
                    <input
                      type="checkbox"
                      checked={payMethodLabel === label}
                      onChange={() => setPayMethodLabel(label)}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </Row>

            <Row label="본문/비고">
              <textarea
                rows={4}
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                placeholder="청구 관련 메모를 입력하세요."
              />
            </Row>

            <div className={styles.actions}>
              <button type="submit" disabled={saving} className={styles.primaryBtn}>
                {saving ? "저장 중..." : "저장"}
              </button>
            </div>

            <div className={styles.actions}>
              <button type="button" onClick={() => download("docx")} className={styles.primaryBtn}>한글(DOCX)</button>
              <button type="button" onClick={() => download("pdf")} className={styles.primaryBtn}>PDF</button>
            </div>
          </form>
        )}
        {activeTab === "invoice" && <InvoiceForm />}
        {activeTab === "statement" && <StatementForm />}
      </div>

      {/* ✅ 기성계 불러오기 모달 */}
      {showGisungModal && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
            zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
          }}
          {...gisungBackdrop}
        >
          <div
            style={{
              background: "#fff", borderRadius: 12, padding: 20,
              width: "min(792px, 95vw)", maxHeight: "80vh", overflow: "auto",
              boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
            }}
          >

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 8 }}>
              <h3 style={{ margin: 0, whiteSpace: "nowrap" }}>기성계 이력 불러오기</h3>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <select
                  value={gisungYear}
                  onChange={(e) => setGisungYear(e.target.value)}
                  style={{ width: 100, height: 34, padding: "0 8px" }}
                >
                  {[2023, 2024, 2025, 2026].map((y) => (
                    <option key={y} value={y}>{y}년</option>
                  ))}
                </select>
                <select
                  value={gisungMonth}
                  onChange={(e) => setGisungMonth(e.target.value)}
                  style={{ width: 90, height: 34, padding: "0 8px" }}
                >
                  <option value="">전체 월</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={m}>{m}월</option>
                  ))}
                </select>
              </div>
            </div>
            



            {gisungLoading ? (
              <div style={{ padding: 24, textAlign: "center", color: "#888" }}>불러오는 중...</div>
            ) : filteredGisungRecords.length === 0 ? (
              <div style={{ padding: 24, textAlign: "center", color: "#aaa" }}>해당 조건의 기성계 이력이 없습니다.</div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    {["기성월", "회차", "거래처(발주처)", "용역명", "금회기성금액", ""].map((h, i) => (
                      <th key={i} style={{ background: "#f1f5f9", padding: "8px 10px", textAlign: "left", borderBottom: "2px solid #d1d5db", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredGisungRecords.map((rec) => (
                    <tr key={rec.id} style={{ borderBottom: "1px solid #eee" }}>
                      <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>
                        {rec.gisung_year}.{String(rec.gisung_month).padStart(2, "0")}
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "center" }}>{rec.gisung_round}회차</td>
                      <td style={{ padding: "8px 10px" }}>{rec.institution || "-"}</td>
                      <td style={{ padding: "8px 10px" }}>{rec.service_name || "-"}</td>
                      <td style={{ padding: "8px 10px", textAlign: "right", whiteSpace: "nowrap" }}>
                        {Number(rec.current_amount).toLocaleString()}원
                      </td>

                      <td style={{ padding: "8px 10px", textAlign: "center" }}>
                        <button
                          type="button"
                          onClick={() => pickGisungRecord(rec)}
                          disabled={savedGisungIds.includes(rec.id)}
                          className={styles.primaryBtn}
                          style={{
                            padding: "5px 12px", fontSize: 12, whiteSpace: "nowrap",
                            background: savedGisungIds.includes(rec.id) ? "#9ca3af" : undefined,
                            cursor: savedGisungIds.includes(rec.id) ? "default" : "pointer",
                          }}
                        >
                          {savedGisungIds.includes(rec.id) ? "✓ 완료" : "불러오기"}
                        </button>
                      </td>


                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div style={{ textAlign: "right", marginTop: 14 }}>
              <button
                type="button"
                onClick={() => setShowGisungModal(false)}
                className={styles.primaryBtn}
                style={{ background: "#6b7280", padding: "8px 16px" }}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );


}
// ✅ 연/월/일 3칸 선택 (연도 미리, 월·일은 빈칸)
function YmdPicker({ y, m, d, setY, setM, setD }) {
  const years = [];
  const cur = new Date().getFullYear();
  for (let yy = cur + 1; yy >= cur - 5; yy--) years.push(yy);

  const box = { height: 36, border: "1px solid #d1d5db", borderRadius: 6, padding: "0 8px", fontSize: 14, boxSizing: "border-box" };

  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
      <select value={y} onChange={(e) => setY(e.target.value)} style={{ ...box, width: 90 }}>
        {years.map((yy) => <option key={yy} value={yy}>{yy}년</option>)}
      </select>
      <select value={m} onChange={(e) => setM(e.target.value)} style={{ ...box, width: 70 }}>
        <option value="">월</option>
        {Array.from({ length: 12 }, (_, i) => i + 1).map((mm) => (
          <option key={mm} value={mm}>{mm}월</option>
        ))}
      </select>
      <select value={d} onChange={(e) => setD(e.target.value)} style={{ ...box, width: 70 }}>
        <option value="">일</option>
        {Array.from({ length: 31 }, (_, i) => i + 1).map((dd) => (
          <option key={dd} value={dd}>{dd}일</option>
        ))}
      </select>
    </div>
  );
}

// ✅ 공통 Row
function Row({ label, children }) {
  return (
    <div className={styles.row}>
      <div className={styles.rowLabel}>{label}</div>
      <div className={styles.rowField}>{children}</div>
    </div>
  );
}

// ✅ 날짜 포맷 함수
function toISO(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
